// UC CDR → Analytics. Where the minutes and the money actually go.
//
// Four league tables over the same window: Partners (who drives the traffic),
// Destinations and Countries (where the money goes) and Carriers (which trunk
// carried it). Click any row to open it up.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronRight, Globe2, Loader2, Building2, RefreshCw, Router, Users,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Pill, Stat, money } from "./ui";
import { TrendStrip } from "./Trend";

const VIEWS = [
  ["partner", "Partners", Users, "Territory on the CDR, matched to the partner org", "Partner"],
  ["destination", "Destinations", Globe2, "The rate sheet's region for the digits dialled", "Destination"],
  ["country", "Countries", Globe2, "Destinations rolled up to the country they belong to", "Country"],
  ["carrier", "Carriers", Router, "The SBC trunk the leg went out on", "Carrier"],
];
const BY_LABEL = { domain: "Domains", destination: "Destinations", carrier: "Carriers",
                   partner: "Partners" };

const num = (n) => (n || 0).toLocaleString();
const minutes = (n) => `${Math.round(n || 0).toLocaleString()} min`;

function Bar({ share, tone }) {
  return (
    <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${tone} transition-[width] duration-500`}
           style={{ width: `${Math.max(1.5, share * 100)}%` }} />
    </div>
  );
}

export default function Analytics({ window: win }) {
  const [dim, setDim] = useState("partner");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  const [by, setBy] = useState("");
  const [drill, setDrill] = useState(null);
  const drills = data?.drills || [];

  const query = useMemo(() => ({
    start: win?.from_at || "", end: win?.to_at || "", tz: win?.tz || "UTC",
  }), [win]);

  const load = useCallback(async () => {
    if (!win) return;
    setBusy("load");
    try {
      const { data: d } = await api.get("/uc-cdr/analytics", {
        params: { ...query, dim, limit: 40 },
      });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the analytics");
    } finally {
      setBusy("");
    }
  }, [win, dim, query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOpen(null); setDrill(null); setBy(""); }, [dim, win]);

  const openRow = async (row, nextBy) => {
    const key = row.raw_key;
    if (!nextBy) return;
    setOpen(row);
    setBy(nextBy);
    setDrill(null);
    try {
      const { data: d } = await api.get("/uc-cdr/analytics/drill", {
        params: { ...query, dim, key, by: nextBy, limit: 20 },
      });
      setDrill(d);
    } catch {
      toast.error("Could not open that row");
    }
  };

  const rebuild = async () => {
    setBusy("rebuild");
    try {
      const { data: d } = await api.post("/uc-cdr/analytics/rebuild", null, { params: query });
      toast.success(d.built
        ? `Summed up ${d.built} hour${d.built === 1 ? "" : "s"}`
          + (d.pending ? ` · ${num(d.pending)} still queued for the cron` : "")
        : "Everything in this window was already summed up");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sum up the window");
    } finally {
      setBusy("");
    }
  };

  const rows = data?.rows || [];
  const priced = (data?.totals || []).filter((t) => t.currency && t.amount);
  // Billable legs that carry no price yet — an inbound leg is zero-rated on
  // purpose and must not be counted as a gap.
  const unrated = (data?.totals || []).reduce((a, t) => a + (t.unrated || 0), 0);
  const cov = data?.coverage || {};
  const peakMinutes = Math.max(...rows.map((r) => r.minutes), 1);
  const peakAmount = Math.max(...rows.map((r) => r.amount), 0.0001);
  const view = VIEWS.find(([v]) => v === dim) || VIEWS[0];

  return (
    <div className="space-y-4" data-testid="uc-cdr-analytics">
      <div className="flex flex-wrap items-center gap-2">
        {VIEWS.map(([value, label, Icon]) => (
          <Button key={value} size="sm" variant={dim === value ? "default" : "outline"}
                  onClick={() => setDim(value)} data-testid={`uc-cdr-analytics-view-${value}`}>
            <Icon className="h-4 w-4" /> {label}
          </Button>
        ))}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={load} disabled={!!busy}
                data-testid="uc-cdr-analytics-refresh">
          {busy === "load" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </Button>
      </div>

      <p className="max-w-3xl text-sm text-slate-500" data-testid="uc-cdr-analytics-note">
        {view[3]}. Figures are pre-summed by the hour, so they line up exactly with every preset
        above{data?.snapped ? " — this window's edges are rounded outwards to whole hours" : ""}.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={`${view[1]} with traffic`} value={num(data?.total_keys ?? rows.length)}
              hint={(data?.total_keys || 0) > rows.length ? `showing the top ${rows.length}` : (win?.label || "")}
              testid="uc-cdr-analytics-stat-rows" />
        <Stat label="Call legs" value={num((data?.totals || []).reduce((a, t) => a + t.legs, 0))}
              testid="uc-cdr-analytics-stat-legs" />
        <Stat label="Minutes" value={minutes((data?.totals || []).reduce((a, t) => a + t.minutes, 0))}
              testid="uc-cdr-analytics-stat-minutes" />
        <Stat label="Rated" tone="text-emerald-700" testid="uc-cdr-analytics-stat-amount"
              value={priced.length ? priced.map((t) => money(t.amount, t.currency)).join(" · ") : "—"}
              hint={unrated ? `${num(unrated)} billable legs still to price` : ""} />
      </div>

      {cov.pending > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-sky-200 bg-sky-50/60 px-4 py-3"
              data-testid="uc-cdr-analytics-coverage">
          <div className="text-sm text-sky-900">
            {num(cov.pending)} hour{cov.pending === 1 ? "" : "s"} of this window changed since they
            were last summed — the figures below are missing that traffic.
          </div>
          <Button size="sm" variant="outline" onClick={rebuild} disabled={!!busy}
                  data-testid="uc-cdr-analytics-rebuild">
            {busy === "rebuild" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sum them up now"}
          </Button>
        </Card>
      )}

      {dim === "carrier" && (
        <p className="max-w-3xl rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-600"
           data-testid="uc-cdr-analytics-carrier-note">
          Trunk data only exists on the SBC side, so this counts SBC legs only. The amount is the
          <strong> revenue</strong> on legs that went out on that trunk — and only where the SBC is the
          rating source for the domain, since a UC Platform domain is priced from its own record.
          Carrier <em>cost</em> would need a wholesale rate sheet, which isn't loaded.
        </p>
      )}

      <TrendStrip dim={dim} params={query}
                  currency={priced.length === 1 ? priced[0].currency : ""} />

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="uc-cdr-analytics-table">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 w-8">#</th>
              <th className="px-3 py-2">{view[4]}</th>
              <th className="px-3 py-2 text-right">Legs</th>
              <th className="px-3 py-2 text-right">In / Out</th>
              <th className="px-3 py-2">Minutes</th>
              <th className="px-3 py-2">Spend</th>
              <th className="px-3 py-2 w-8" />
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-analytics-body">
            {rows.map((r, i) => (
              <tr key={r.key}
                  className={`border-t border-slate-100 transition-colors hover:bg-slate-50/80 ${drills.length ? "cursor-pointer" : ""}`}
                  onClick={() => drills.length && openRow(r, by || drills[0])}
                  data-testid={`uc-cdr-analytics-row-${r.raw_key || "none"}`}>
                <td className="px-3 py-2 text-[11px] text-slate-400">{i + 1}</td>
                <td className="px-3 py-2">
                  <span className="font-medium text-slate-800">{r.key}</span>
                  {!r.raw_key && <Pill tone="bg-amber-100 text-amber-800">no match</Pill>}
                </td>
                <td className="px-3 py-2 text-right text-slate-600">{num(r.legs)}</td>
                <td className="px-3 py-2 text-right text-[12px] text-slate-500">
                  {num(r.inbound)} / {num(r.outbound)}
                </td>
                <td className="px-3 py-2">
                  <div className="text-slate-700">{minutes(r.minutes)}</div>
                  <Bar share={r.minutes / peakMinutes} tone="bg-sky-400" />
                </td>
                <td className="px-3 py-2">
                  <div className={r.money?.length ? "text-slate-800" : "text-slate-400"}>
                    {r.money?.length
                      ? r.money.map((m) => money(m.amount, m.currency)).join(" · ")
                      : "—"}
                  </div>
                  {!!r.unrated && (
                    <div className="text-[11px] text-amber-600">{num(r.unrated)} to price</div>
                  )}
                  <Bar share={r.amount / peakAmount} tone="bg-emerald-400" />
                </td>
                <td className="px-3 py-2 text-slate-300">
                  {!!drills.length && <ChevronRight className="h-4 w-4" />}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-analytics-empty">
                {busy === "load" ? "Working…"
                  : cov.pending
                    ? "Nothing summed up for this window yet — press \"Sum them up now\" above."
                    : "No traffic in this window."}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {open && (
        <Card className="overflow-hidden" data-testid="uc-cdr-analytics-drill">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <Building2 className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">{open.key}</span>
            <span className="text-[12px] text-slate-400">
              {num(open.legs)} legs · {minutes(open.minutes)}
            </span>
            <div className="ml-auto flex gap-1.5">
              {drills.map((value) => (
                <Button key={value} size="sm" variant={by === value ? "default" : "outline"}
                        className="h-7 text-[12px]" onClick={() => openRow(open, value)}
                        data-testid={`uc-cdr-analytics-by-${value}`}>
                  {BY_LABEL[value] || value}
                </Button>
              ))}
              <Button size="sm" variant="ghost" className="h-7 text-[12px]"
                      onClick={() => { setOpen(null); setDrill(null); }}
                      data-testid="uc-cdr-analytics-drill-close">Close</Button>
            </div>
          </div>
          {!drill ? (
            <div className="px-4 py-6 text-sm text-slate-500">Working it out…</div>
          ) : !drill.rows?.length ? (
            <div className="px-4 py-6 text-sm text-slate-500" data-testid="uc-cdr-analytics-drill-empty">
              {drill.note || "Nothing to break down here."}
            </div>
          ) : (
            <table className="w-full text-left text-sm" data-testid="uc-cdr-analytics-drill-body">
              <tbody>
                {drill.rows.map((r) => (
                  <tr key={r.key} className="border-t border-slate-100">
                    <td className="px-4 py-1.5 text-slate-700">{r.key}</td>
                    <td className="px-4 py-1.5 text-right text-[12px] text-slate-500">{num(r.legs)} legs</td>
                    <td className="px-4 py-1.5 text-right text-slate-600">{minutes(r.minutes)}</td>
                    <td className="px-4 py-1.5 text-right text-slate-800">
                      {r.money?.length
                        ? r.money.map((m) => money(m.amount, m.currency)).join(" · ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
